<template>
  <v-container fluid>
    <div v-if="yearItems.length > 1">
      <v-row>
        <v-col v-for="(chart, cIndex) in chartGrid" :key="cIndex" cols="12" lg="6">
          <PlotlyWrapper :data="chart.data" :layout="chart.layout"></PlotlyWrapper>
        </v-col>
      </v-row>
    </div>

    <v-card v-else flat>
      <v-row>
        <v-col cols="12" lg="4">
          <v-row>
            <v-col cols="6" lg="12">
              <v-card flat>
                <v-card-title>{{ outputs.elccPercentage[0].toFixed(1) }} %</v-card-title>
                <v-card-text>ELCC</v-card-text>
              </v-card>
            </v-col>
            <v-col cols="6" lg="12">
              <v-card flat>
                <v-card-title>{{ outputs.elccMegawatts[0] }} MW</v-card-title>
                <v-card-text>Qualifiying Capacity</v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" lg="8">
          <v-data-table :headers="tableHeaders" :items="singleYearTableItems" hide-default-footer>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>
    <v-divider></v-divider>
    <v-card>
      <v-card-text>
        <v-row justify="end">
          <v-col cols="12" lg="2">
            <v-select
              width="200"
              v-model="selectedYear"
              label="Year"
              :items="yearItems"
              item-text="year"
              item-value="index"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col v-for="(chart, cIndex) in yearlyChartGrid" :key="cIndex" cols="12" lg="6">
            <PlotlyWrapper :data="chart.data" :layout="chart.layout"></PlotlyWrapper>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "ELCCDashboard",
  components: {
    PlotlyWrapper: () => import("@/components/charts/PlotlyWrapper.vue"),
  },
  mixins: [],
  props: {
    outputs: { type: Object, required: true },
    startYear: { type: Number, required: true },
  },
  data() {
    return {
      defaultLayout: {
        xaxis: {
          tickmode: "linear",
          tick0: this.startYear,
          dtick: 1,
          title: "Year",
        },
      },
      hours: {
        text: Array.from({ length: 24 }, (v, i) => i.toString().padStart(2, "0")),
        value: Array.from({ length: 24 }, (v, i) => i),
      },
      months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      selectedYear: 0,
      colorscale: [
        [0, "#f8f8f8"],
        [1, "#f80000"],
      ],
      tableHeaders: [
        { text: "Name", value: "name" },
        { text: "With Asset", value: "asset" },
        { text: "Without Asset", value: "withoutAsset" },
      ],
    };
  },
  computed: {
    singleYearTableItems() {
      return [
        {
          name: "LOLE (days per year)",
          asset: this.outputs.loleValues[0].withAsset.toFixed(4),
          withoutAsset: this.outputs.loleValues[0].withoutAsset.toFixed(4),
        },
        {
          name: "Annual Load (GHh)",
          asset: this.outputs.annualLoad[0].withAsset.toFixed(1),
          withoutAsset: this.outputs.annualLoad[0].withoutAsset.toFixed(1),
        },
        {
          name: "Annual Peak (MW)",
          asset: this.outputs.annualPeak[0].withAsset,
          withoutAsset: this.outputs.annualPeak[0].withoutAsset,
        },
      ];
    },
    chartGrid() {
      return [
        {
          data: this.elcc,
          layout: { ...this.defaultLayout, title: "ELCC", yaxis: { title: "ELCC Rating (%)" } },
        },
        {
          data: this.qualifiyingCapacity,
          layout: {
            ...this.defaultLayout,
            title: "Qualifiying Capacity",
            yaxis: { title: "MW" },
          },
        },
        {
          data: this.loleAsset,
          layout: {
            ...this.defaultLayout,
            title: "LOLE Asset Comparison",
            yaxis: { title: "days per year" },
          },
        },
        {
          data: this.annualLoadAsset,
          layout: {
            ...this.defaultLayout,
            title: "Annual Load Asset Comparison",
            yaxis: { title: "GWh" },
          },
        },
        {
          data: this.annualPeakAsset,
          layout: {
            ...this.defaultLayout,
            title: "Annual Peak Asset Comparison",
            yaxis: { title: "MW" },
          },
        },
      ];
    },
    yearlyChartGrid() {
      return [
        {
          data: this.outagewithAsset,
          layout: {
            title: "Outage Prob. with Asset",
            xaxis: {
              title: "Hours",
              tickmode: "array",
              tickvals: this.hours.value,
              ticktext: this.hours.text,
            },
          },
        },
        {
          data: this.outagewithoutAsset,
          layout: {
            title: "Outage Prob. without Asset",
            xaxis: {
              title: "Hours",
              tickmode: "array",
              tickvals: this.hours.value,
              ticktext: this.hours.text,
            },
          },
        },
        {
          data: this.durationCurveWithAsset,
          layout: {
            title: "Duration Curves with Asset",
            xaxis: { title: "Hour" },
            yaxis: { title: "MW" },
          },
        },
        {
          data: this.durationCurveWithoutAsset,
          layout: {
            title: "Duration Curves without Asset",
            xaxis: { title: "Hour" },
            yaxis: { title: "MW" },
          },
        },
        {
          data: this.deltaDurationCurve,
          layout: {
            title: "Delta Duration Curves",
            xaxis: { title: "Hour" },
            yaxis: { title: "MW" },
          },
        },
        // {
        //   data: this.stackedDurationCurve,
        //   layout: {
        //     title: "Stacked Duration Curves",
        //     xaxis: { title: "Hour" },
        //     yaxis: { title: "MW" },
        //   },
        // },
        {
          data: this.lolePerLoadStep,
          layout: {
            title: "LOLE per Load Step Asset Comparison",
            xaxis: {
              title: "Load step",
            },
            yaxis: {
              title: "LOLE",
            },
          },
        },
      ];
    },
    yearItems() {
      return this.outputs.elccPercentage.map((_, index) => ({
        year: index + this.startYear,
        index,
      }));
    },
    years() {
      return this.yearItems.map((item) => item.year);
    },
    // Used inside chartGrid
    elcc() {
      return [
        {
          x: this.years,
          y: this.outputs.elccPercentage,
          type: "lines+markers",
        },
      ];
    },
    qualifiyingCapacity() {
      return [
        {
          x: this.years,
          y: this.outputs.elccMegawatts,
          type: "lines+markers",
        },
      ];
    },
    loleAsset() {
      return [
        {
          x: this.years,
          y: this.outputs.loleValues.map((year) => year.withAsset),
          type: "lines+markers",
          name: "With asset",
        },
        {
          x: this.years,
          y: this.outputs.loleValues.map((year) => year.withoutAsset),
          type: "lines+markers",
          name: "Without asset",
        },
      ];
    },
    annualLoadAsset() {
      return [
        {
          x: this.years,
          y: this.outputs.annualLoad.map((year) => year.withAsset),
          type: "lines+markers",
          name: "With asset",
        },
        {
          x: this.years,
          y: this.outputs.annualLoad.map((year) => year.withoutAsset),
          type: "lines+markers",
          name: "Without asset",
        },
      ];
    },
    annualPeakAsset() {
      return [
        {
          x: this.years,
          y: this.outputs.annualPeak.map((year) => year.withAsset),
          type: "lines+markers",
          name: "With asset",
        },
        {
          x: this.years,
          y: this.outputs.annualPeak.map((year) => year.withoutAsset),
          type: "lines+markers",
          name: "Without asset",
        },
      ];
    },
    // Used inside yearlyChartGrid
    selectedYearMaxOutage() {
      const { withAsset, withoutAsset } = this.outputs.outageLikelyPerTime[this.selectedYear];
      const combinedArray = [...withAsset.flat(), ...withoutAsset.flat()];
      return Math.max(...combinedArray);
    },
    outagewithAsset() {
      return [
        {
          z: this.outputs.outageLikelyPerTime[this.selectedYear].withAsset,
          x: this.hours.value,
          y: this.months,
          type: "heatmap",
          hoverongaps: false,
          colorscale: this.colorscale,
          showscale: false,
          zmax: this.selectedYearMaxOutage,
          zmin: 0,
          hovertemplate: "Month: %{y}<br>Hour: %{x}:00<br>%{z}<extra></extra>",
        },
      ];
    },
    outagewithoutAsset() {
      return [
        {
          z: this.outputs.outageLikelyPerTime[this.selectedYear].withoutAsset,
          x: this.hours.value,
          y: this.months,
          type: "heatmap",
          hoverongaps: false,
          colorscale: this.colorscale,
          showscale: false,
          zmax: this.selectedYearMaxOutage,
          zmin: 0,
          hovertemplate: "Month: %{y}<br>Hour: %{x}:00<br>%{z}<extra></extra>",
        },
      ];
    },
    durationCurveWithAsset() {
      const charts = [
        {
          atribute: "totalDemand",
          name: "Total Demand",
          fill: "tozeroy",
          mode: "none",
          fillcolor: "#1F77B4",
        },
        {
          atribute: "netDemand",
          name: "Net demand",
          fill: "tozeroy",
          mode: "none",
          fillcolor: "#FF7F0E",
        },
        { atribute: "marketContribution", name: "Market contribution", mode: "lines" },
        { atribute: "renewableContribution", name: "Renewable contribution", mode: "lines" },
        { atribute: "storageContribution", name: "Storage contribution", mode: "lines" },
      ];

      return charts.map((chart) => ({
        x: this.outputs.durationCurves[this.selectedYear].withAsset[chart.atribute].map(
          (_, index) => index
        ),
        y: this.outputs.durationCurves[this.selectedYear].withAsset[chart.atribute].toSorted(
          (a, b) => b - a
        ),
        name: chart.name,
        fill: chart.fill,
        mode: chart.mode,
        fillcolor: chart.fillcolor,
      }));
    },
    durationCurveWithoutAsset() {
      const charts = [
        {
          atribute: "totalDemand",
          name: "Total Demand",
          fill: "tozeroy",
          mode: "none",
          fillcolor: "#1F77B4",
        },
        {
          atribute: "netDemand",
          name: "Net demand",
          fill: "tozeroy",
          mode: "none",
          fillcolor: "#FF7F0E",
        },
        { atribute: "marketContribution", name: "Market contribution", mode: "lines" },
        { atribute: "renewableContribution", name: "Renewable contribution", mode: "lines" },
        { atribute: "storageContribution", name: "Storage contribution", mode: "lines" },
      ];

      return charts.map((chart) => ({
        x: this.outputs.durationCurves[this.selectedYear].withoutAsset[chart.atribute].map(
          (_, index) => index
        ),
        y: this.outputs.durationCurves[this.selectedYear].withoutAsset[chart.atribute].toSorted(
          (a, b) => b - a
        ),
        name: chart.name,
        fill: chart.fill,
        mode: chart.mode,
        fillcolor: chart.fillcolor,
      }));
    },
    stackedDurationCurve() {
      const data = [];
      const yearDurationCurves = this.outputs.durationCurves[this.selectedYear];
      const contributions = [
        { atribute: "marketContribution", name: "Market contribution" },
        { atribute: "renewableContribution", name: "Renewable contribution" },
        { atribute: "storageContribution", name: "Storage contribution" },
      ];

      data.push({
        x: yearDurationCurves.withoutAsset.totalDemand.map((_, index) => index),
        y: yearDurationCurves.withoutAsset.totalDemand.toSorted((a, b) => b - a),
        name: "Total Demand",
      });

      const netDemand = yearDurationCurves.withoutAsset.netDemand.map((value, index) => ({
        value,
        key: index,
      }));

      netDemand.sort((a, b) => b.value - a.value);

      data.push({
        x: yearDurationCurves.withoutAsset.netDemand.map((_, index) => index),
        y: netDemand.map((object) => object.value),
        name: "Net Demand",
        stackgroup: "one",
      });

      contributions.forEach((contribution) => {
        data.push({
          x: yearDurationCurves.withoutAsset[contribution.atribute].map((_, index) => index),
          y: netDemand.map(
            (object) => yearDurationCurves.withoutAsset[contribution.atribute][object.key]
          ),
          name: contribution.name,
          stackgroup: "one",
        });
      });

      return data;
    },
    deltaDurationCurve() {
      const charts = [
        { atribute: "netDemand", name: "Net demand" },
        { atribute: "marketContribution", name: "Market contribution" },
        { atribute: "renewableContribution", name: "Renewable contribution" },
        { atribute: "storageContribution", name: "Storage contribution" },
      ];

      const yearDurationCurve = this.outputs.durationCurves[this.selectedYear];
      const x = yearDurationCurve.withoutAsset.totalDemand.map((_, i) => i);
      const deltaCurves = [];

      charts.forEach((chart) => {
        const withoutAsset = yearDurationCurve.withoutAsset[chart.atribute].map((value, index) => ({
          value,
          key: index,
        }));
        const withAsset = yearDurationCurve.withAsset[chart.atribute];
        withoutAsset.sort((a, b) => b.value - a.value);
        const withoutAssetCompared = withoutAsset.map(
          (object) => object.value - withAsset[object.key]
        );
        deltaCurves.push({
          x,
          y: withoutAssetCompared,
          name: chart.name,
        });
      });
      return deltaCurves;
    },
    lolePerLoadStep() {
      return [
        {
          x: this.outputs.lolePerLoadStep[this.selectedYear].withAsset.peakLoad,
          y: this.outputs.lolePerLoadStep[this.selectedYear].withAsset.lole,
          type: "lines+markers",
          name: "With asset",
        },
        {
          x: this.outputs.lolePerLoadStep[this.selectedYear].withoutAsset.peakLoad,
          y: this.outputs.lolePerLoadStep[this.selectedYear].withoutAsset.lole,
          type: "lines+markers",
          name: "Without asset",
        },
      ];
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    listSubstraction(list1, list2) {
      return list1.map((value, index) => value - list2[index]);
    },
  },
};
</script>

<style></style>
